<script setup>
import { ref, computed, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import * as authApi from '@/api/auth'
import * as validation from '@/utils/validation'

const router = useRouter()

const authStore = useAuthStore()

// has_password 响应式，设置成功后自动切换到「修改密码」视图
const hasPassword = computed(() => authStore.userInfo?.has_password)

// ── Tab: 设置密码（验证码登录用户，直接填新密码，无需验证码）──────────
const setForm = ref({ newPassword: '', confirmPassword: '' })
const setLoading = ref(false)
const setError = ref('')
const setSuccess = ref('')
const setPasswordErrors = ref([])

const handleSetPassword = async () => {
  setError.value = ''
  setSuccess.value = ''
  setPasswordErrors.value = []

  const pwdValidation = validation.validatePassword(setForm.value.newPassword)
  if (!pwdValidation.isValid) {
    setPasswordErrors.value = pwdValidation.errors
    setError.value = pwdValidation.errors[0]
    return
  }
  if (!validation.validatePasswordMatch(setForm.value.newPassword, setForm.value.confirmPassword)) {
    setError.value = validation.getPasswordMatchErrorMessage()
    return
  }

  setLoading.value = true
  try {
    const res = await authApi.setPassword(authStore.phone, setForm.value.newPassword, setForm.value.confirmPassword)
    if (res.code === 1) {
      authStore.updateUserInfo({ has_password: true })
      router.push('/applications')
    } else {
      setError.value = res.message || '设置失败，请重试'
    }
  } catch {
    setError.value = '设置失败，请检查网络连接'
  } finally {
    setLoading.value = false
  }
}

// ── Tab: 修改密码（已有密码用户，需要验证码验证身份）────────────────────
const changeForm = ref({ code: '', newPassword: '', confirmPassword: '' })
const changeLoading = ref(false)
const changeError = ref('')
const changeSuccess = ref('')
const changePasswordErrors = ref([])
const codeButtonLoading = ref(false)
const countdown = ref(0)
let timer = null

const sendCode = async () => {
  changeError.value = ''
  codeButtonLoading.value = true
  try {
    const res = await authApi.sendVerificationCode(authStore.phone, 3) // 3 = reset password
    if (res.code === 1) {
      countdown.value = 60
      timer = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) clearInterval(timer)
      }, 1000)
    } else {
      changeError.value = res.message || '验证码发送失败'
    }
  } catch {
    changeError.value = '验证码发送失败，请检查网络连接'
  } finally {
    codeButtonLoading.value = false
  }
}

const handleChangePassword = async () => {
  changeError.value = ''
  changeSuccess.value = ''
  changePasswordErrors.value = []

  if (!validation.validateCode(changeForm.value.code)) {
    changeError.value = validation.getCodeErrorMessage()
    return
  }
  const pwdValidation = validation.validatePassword(changeForm.value.newPassword)
  if (!pwdValidation.isValid) {
    changePasswordErrors.value = pwdValidation.errors
    changeError.value = pwdValidation.errors[0]
    return
  }
  if (!validation.validatePasswordMatch(changeForm.value.newPassword, changeForm.value.confirmPassword)) {
    changeError.value = validation.getPasswordMatchErrorMessage()
    return
  }

  changeLoading.value = true
  try {
    const res = await authApi.resetPassword(authStore.phone, changeForm.value.code, changeForm.value.newPassword)
    if (res.code === 1) {
      changeSuccess.value = '密码修改成功'
      changeForm.value = { code: '', newPassword: '', confirmPassword: '' }
    } else {
      changeError.value = res.message || '修改失败，请重试'
    }
  } catch {
    changeError.value = '修改失败，请检查网络连接'
  } finally {
    changeLoading.value = false
  }
}

onUnmounted(() => { if (timer) clearInterval(timer) })
</script>

<template>
  <div class="settings-container">
    <div class="settings-card">

      <!-- 未设置过密码的用户提示 -->
      <div v-if="hasPassword === false" class="tip-banner">
        💡 您还未设置登录密码，建议设置后下次可直接用密码登录
      </div>

      <!-- Tabs -->
      <div class="tabs">
        <div v-if="hasPassword === false" class="tab-item active">
          设置密码
        </div>
        <div v-else class="tab-item active">
          修改密码
        </div>
      </div>

      <!-- ── 设置密码：直接填新密码，无需验证码 ── -->
      <div v-if="hasPassword === false">
        <p class="instruction">直接设置登录密码，无需验证码。</p>

        <div v-if="setError" class="error-message">{{ setError }}</div>
        <div v-if="setSuccess" class="success-message">✅ {{ setSuccess }}</div>

        <form @submit.prevent="handleSetPassword">
          <div class="form-group">
            <label>新密码</label>
            <input
              type="password"
              v-model="setForm.newPassword"
              placeholder="至少8位，包含大小写字母和数字"
              :disabled="setLoading"
              required
            />
            <div v-if="setPasswordErrors.length > 0" class="password-hints">
              <div v-for="(err, idx) in setPasswordErrors" :key="idx" class="hint-item">{{ err }}</div>
            </div>
          </div>
          <div class="form-group">
            <label>确认密码</label>
            <input
              type="password"
              v-model="setForm.confirmPassword"
              placeholder="请再次输入新密码"
              :disabled="setLoading"
              required
            />
          </div>
          <div class="btn-row">
            <button type="submit" class="btn-submit" :disabled="setLoading">
              {{ setLoading ? '提交中...' : '确认设置' }}
            </button>
            <button type="button" class="btn-skip" @click="router.push('/applications')" :disabled="setLoading">
              暂不设置
            </button>
          </div>
        </form>
      </div>

      <!-- ── 修改密码：需要验证码验证身份 ── -->
      <div v-if="hasPassword !== false">
        <p class="instruction">通过手机验证码验证身份后修改密码。</p>

        <div v-if="changeError" class="error-message">{{ changeError }}</div>
        <div v-if="changeSuccess" class="success-message">✅ {{ changeSuccess }}</div>

        <form @submit.prevent="handleChangePassword">
          <div class="form-group">
            <label>手机号</label>
            <div class="input-readonly">{{ authStore.phone }}</div>
          </div>
          <div class="form-group">
            <label>验证码</label>
            <div class="code-wrapper">
              <input
                type="text"
                v-model="changeForm.code"
                placeholder="6位数字验证码"
                maxlength="6"
                :disabled="changeLoading"
                required
              />
              <button
                type="button"
                class="btn-code"
                @click="sendCode"
                :disabled="countdown > 0 || codeButtonLoading || changeLoading"
              >
                {{ countdown > 0 ? `${countdown}s` : '获取验证码' }}
              </button>
            </div>
          </div>
          <div class="form-group">
            <label>新密码</label>
            <input
              type="password"
              v-model="changeForm.newPassword"
              placeholder="至少8位，包含大小写字母和数字"
              :disabled="changeLoading"
              required
            />
            <div v-if="changePasswordErrors.length > 0" class="password-hints">
              <div v-for="(err, idx) in changePasswordErrors" :key="idx" class="hint-item">{{ err }}</div>
            </div>
          </div>
          <div class="form-group">
            <label>确认新密码</label>
            <input
              type="password"
              v-model="changeForm.confirmPassword"
              placeholder="请再次输入新密码"
              :disabled="changeLoading"
              required
            />
          </div>
          <button type="submit" class="btn-submit btn-submit-full" :disabled="changeLoading">
            {{ changeLoading ? '提交中...' : '确认修改' }}
          </button>
        </form>
      </div>

    </div>
  </div>
</template>

<style scoped>
.settings-container {
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: #f4f6f9;
  padding: 60px 20px;
}

.settings-card {
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 460px;
  border-top: 4px solid #005eb8;
}

.settings-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #1a1a1a;
  text-align: center;
}

.user-info-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f5f8ff;
  border: 1px solid #d0e4f7;
  border-radius: 6px;
  padding: 10px 16px;
  margin-bottom: 16px;
  font-size: 14px;
}

.user-label { color: #666; }

.user-phone {
  font-weight: 600;
  color: #005eb8;
}

.tip-banner {
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  color: #7c5800;
  margin-bottom: 16px;
  line-height: 1.5;
}

.tabs {
  display: flex;
  border-bottom: 1px solid #eee;
  margin-bottom: 24px;
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 10px 8px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.tab-item:hover { color: #005eb8; }

.tab-item.active {
  color: #005eb8;
  font-weight: 600;
  border-bottom-color: #005eb8;
}

.instruction {
  font-size: 13px;
  color: #888;
  margin-bottom: 20px;
  line-height: 1.5;
}

.error-message {
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  color: #d84315;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
}

.success-message {
  background-color: #f0fff4;
  border: 1px solid #b7eb8f;
  color: #389e0d;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.form-group input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}

.form-group input:focus { border-color: #005eb8; }

.input-readonly {
  padding: 10px 12px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  color: #555;
}

.code-wrapper {
  display: flex;
  gap: 10px;
}

.code-wrapper input { flex: 1; }

.btn-code {
  padding: 0 16px;
  background: #eef4fb;
  color: #005eb8;
  border: 1px solid #cce0f5;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-width: 100px;
  white-space: nowrap;
  transition: all 0.2s;
}

.btn-code:hover:not(:disabled) { background: #dbe9f6; }

.btn-code:disabled {
  background: #f5f5f5;
  color: #999;
  border-color: #ddd;
  cursor: not-allowed;
}

.password-hints {
  background-color: #fef5f0;
  border: 1px solid #ffe7da;
  border-radius: 4px;
  margin-top: 6px;
  padding: 8px 12px;
}

.hint-item {
  font-size: 12px;
  color: #d84315;
  margin: 2px 0;
}

.btn-row {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.btn-submit {
  flex: 1;
  padding: 12px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  font-size: 15px;
  transition: background-color 0.2s;
}

.btn-submit:hover:not(:disabled) { background-color: #004a94; }

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-submit-full {
  width: 100%;
  margin-top: 8px;
}

.btn-skip {
  flex: 1;
  padding: 12px;
  background: white;
  color: #999;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  font-size: 15px;
  transition: all 0.2s;
}

.btn-skip:hover:not(:disabled) {
  color: #666;
  border-color: #ccc;
}
</style>
